import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonComponent } from '../../button/button.component';
import { InputComponent } from '../../input/input.component';
import { formatDateForInput } from '../../../utils/date-utils';
import { dateRangeValidator } from '../../../utils/date-range.validator';
import { WorkExperienceExtended } from '../../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { WORK_EXPERIENCE_VALIDATION } from '../../../../../../../libs/shared/src/lib/constants/validation-constants';
import { Editor, Toolbar, NgxEditorModule } from 'ngx-editor';
import { createSafeEditor } from '../../../../../../../libs/shared/src/lib/editor/safe-editor';

@Component({
  selector: 'app-work-experience-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, InputComponent, TranslocoPipe, NgxEditorModule],
  templateUrl: './work-experience-form.component.html',
  styleUrls: ['./work-experience-form.component.scss'],
})
export class WorkExperienceFormComponent implements OnInit, OnChanges, OnDestroy {
  isCurrentlyWorking = false;

  // Rich text editor for description
  descriptionEditor: Editor;
  htmlDescription = '';
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  @Input() editingWorkExperience: WorkExperienceExtended | null = null;
  @Input() editingIndex: number | null = null;

  @Output() save = new EventEmitter<Partial<WorkExperienceExtended>>();
  @Output() cancel = new EventEmitter<void>();

  workExperienceForm: FormGroup;

  // Expose validation constants to template
  readonly VALIDATION = WORK_EXPERIENCE_VALIDATION;

  constructor(private fb: FormBuilder) {
    this.workExperienceForm = this.createForm();
    this.descriptionEditor = createSafeEditor();
  }

  ngOnInit(): void {
    if (this.editingWorkExperience) {
      this.populateForm(this.editingWorkExperience);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editingWorkExperience'] && changes['editingWorkExperience'].currentValue) {
      this.populateForm(changes['editingWorkExperience'].currentValue);
    }
  }

  ngOnDestroy(): void {
    this.descriptionEditor.destroy();
  }

  /**
   * Create the form with validation
   * Using centralized validation constants to match database constraints
   * @returns FormGroup
   */
  private createForm(): FormGroup {
    return this.fb.group(
      {
        employer: ['', [Validators.required, Validators.maxLength(WORK_EXPERIENCE_VALIDATION.EMPLOYER_MAX_LENGTH)]],
        position: ['', [Validators.required, Validators.maxLength(WORK_EXPERIENCE_VALIDATION.POSITION_MAX_LENGTH)]],
        city: ['', [Validators.maxLength(WORK_EXPERIENCE_VALIDATION.CITY_MAX_LENGTH)]],
        country: ['', [Validators.maxLength(WORK_EXPERIENCE_VALIDATION.COUNTRY_MAX_LENGTH)]],
        description: ['', [Validators.maxLength(WORK_EXPERIENCE_VALIDATION.DESCRIPTION_MAX_LENGTH)]],
        startDate: ['', [Validators.required]],
        endDate: ['', [Validators.required]],
        isCurrentlyWorking: [false],
      },
      { validators: dateRangeValidator({ ongoingKey: 'isCurrentlyWorking' }) }
    );
  }

  private populateForm(workExperience: WorkExperienceExtended): void {
    // Format dates for the date inputs (YYYY-MM-DD format)
    let formattedStartDate = '';
    let formattedEndDate = '';

    // Format dates using centralized utility functions
    if (workExperience.startDate) {
      formattedStartDate = formatDateForInput(workExperience.startDate);
    }

    if (workExperience.endDate) {
      formattedEndDate = formatDateForInput(workExperience.endDate);
    }

    this.workExperienceForm.patchValue({
      employer: workExperience.employer || '',
      position: workExperience.position || '',
      city: workExperience.city || '',
      country: workExperience.country || '',
      description: workExperience.description || '',
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    });

    // Set rich text editor content
    this.htmlDescription = workExperience.description || '';

    // Handle currently working status
    this.isCurrentlyWorking = workExperience.current || false;
    this.workExperienceForm.get('isCurrentlyWorking')?.setValue(this.isCurrentlyWorking);

    // Disable end date if currently working
    if (this.isCurrentlyWorking) {
      this.workExperienceForm.get('endDate')?.disable();
    } else {
      this.workExperienceForm.get('endDate')?.enable();
    }
    this.workExperienceForm.updateValueAndValidity();
  }

  /**
   * Toggle the currently working status
   * @param event Change event
   */
  onCurrentlyWorkingChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.isCurrentlyWorking = target.checked;

    if (this.isCurrentlyWorking) {
      this.workExperienceForm.get('endDate')?.disable();
    } else {
      this.workExperienceForm.get('endDate')?.enable();
    }
    this.workExperienceForm.updateValueAndValidity();
  }

  /**
   * Submit the form
   */
  onSubmit(): void {
    if (this.workExperienceForm.invalid) {
      this.workExperienceForm.markAllAsTouched();

      // Check for specific validation issues
      const controls = this.workExperienceForm.controls;

      // Employer name length is critical - database constraint
      if (controls['employer'].errors?.['maxlength']) {
        console.error('Employer name exceeds maximum length of', WORK_EXPERIENCE_VALIDATION.EMPLOYER_MAX_LENGTH);
        return;
      }

      return;
    }

    // Extra validation check for employer length - database constraint
    const employerValue = this.workExperienceForm.get('employer')?.value;
    if (employerValue && employerValue.length > WORK_EXPERIENCE_VALIDATION.EMPLOYER_MAX_LENGTH) {
      console.error('Employer name exceeds database constraint of', WORK_EXPERIENCE_VALIDATION.EMPLOYER_MAX_LENGTH);
      return;
    }

    const formValue = this.workExperienceForm.getRawValue();

    // Create the work experience object
    const workExperience: Partial<WorkExperienceExtended> = {
      id: this.editingWorkExperience?.id,
      employer: formValue.employer,
      position: formValue.position,
      city: formValue.city,
      country: formValue.country,
      description: this.htmlDescription || formValue.description, // Use rich text content
      startDate: formValue.startDate,
      endDate: this.isCurrentlyWorking ? null : formValue.endDate,
      current: this.isCurrentlyWorking,
    };

    this.save.emit(workExperience);
  }
}
